import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  CircularProgress,
} from "@mui/material";
import DashboardContainer from "../../components/dashboard-container";
import { marketDataService } from "../../utils/services/marketDataService";
import AutocompleteWithPagination from "../../components/autocomplete-with-pagination";

const MarketDataCalculationForm = () => {
  const [formData, setFormData] = useState({
    country: "",
    category: "",
    revenue: "",
  });
  const [calculatedData, setCalculatedData] = useState(null);
  const [loading, setLoading] = useState(false);
  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  const isFormValid = () => {
    return formData.country && formData.category && formData.revenue;
  };
  const estimateMarketData = async (data) => {
    setLoading(true);
    try {
      const response = await marketDataService.estimate(data);
      setLoading(false);
      setCalculatedData({ ...response, category: formData.category.category });
    } catch (error) {
      console.error("estimate failed:", error);
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    estimateMarketData({
      ...formData,
      category: formData.category._id,
    });
  };
  const formatKey = (key) => {
    // Convert camelCase or snake_case to readable format
    return key
      .replace(/([A-Z])/g, " $1") // Add space before capital letters
      .replace(/_/g, " ") // Replace underscores with spaces
      .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize first letter of each word
  };
  const formatValue = (value) => {
    if (typeof value === "number") {
      return parseFloat(value.toFixed(2)); // Round to 2 decimal places
    }
    return value;
  };

  return (
    <DashboardContainer
      hideAddButton
      filterButton={false}
      hideUploadButton
      header="Calculate Market Data"
      hideGridList
      hideFilterClient={true}
    >
      <Box
        sx={{ p: 3, bgcolor: "var(--bg-paper)", borderRadius: 2, boxShadow: 2 }}
      >
        <Grid container spacing={2}>
          <Grid
            sx={{
              alignItems: "flex-start",
              display: "flex",
              flexDirection: "column",
            }}
            item
            xs={12}
            md={6}
          >
            <Typography
              sx={{ textAlign: "start", fontWeight: 600 }}
              variant="h6"
            >
              Market Data Calculation
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{ mt: 2, fontWeight: 500, textAlign: "start" }}
            >
              Ship From Countries <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              size="small"
              select
              fullWidth
              name="country"
              value={formData.country}
              onChange={handleChange}
              required
              placeholder="Select Country"
              sx={{ textAlign: "start", mt: 1, maxWidth: "80%" }} // Ensures left alignment
            >
              {[
                "China",
                "Vietnam",
                "India",
                "Combodia",
                "Bangladesh",
                "Other APAC",
                "Other South America",
              ].map((country) => (
                <MenuItem key={country} value={country}>
                  {country}
                </MenuItem>
              ))}
            </TextField>

            <Typography
              variant="subtitle1"
              sx={{ mt: 2, fontWeight: 500, textAlign: "start" }}
            >
              Category <span style={{ color: "red" }}>*</span>
            </Typography>
            <div style={{ width: "80%" }}>
              <AutocompleteWithPagination
                selectedItem={formData.category}
                onDataChange={(e) =>
                  handleChange({
                    target: {
                      name: "category",
                      value: e,
                    },
                  })
                }
                hideLabel
                width={"100%"}
                optionKey={"category"}
                apiEndPoint={(limit, skip, search, client_id) =>
                  marketDataService.fetch(limit, skip, client_id, search)
                }
              ></AutocompleteWithPagination>
            </div>
            <Typography
              variant="subtitle1"
              sx={{ mt: 2, fontWeight: 500, textAlign: "start" }}
            >
              Revenue <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              sx={{ mt: 1, maxWidth: "80%" }}
              size="small"
              fullWidth
              name="revenue"
              type="number"
              value={formData.revenue}
              onChange={handleChange}
              required
              placeholder="Enter Revenue"
            />
            <div style={{ display: "flex" }}>
              <Button
                variant="contained"
                color="error"
                onClick={handleSubmit}
                sx={{ mt: 3, display: "flex", justifyContent: "flex-start" }}
                disabled={!isFormValid()}
              >
                Calculate
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={() => {
                  setFormData({ country: "", category: "", revenue: "" });
                  setCalculatedData(null);
                }}
                disabled={!isFormValid()}
                sx={{
                  mt: 3,
                  display: "flex",
                  justifyContent: "flex-start",
                  ml: 2,
                }}
              >
                Cancel
              </Button>
            </div>
          </Grid>

          {loading ? (
            <CircularProgress color="inherit" />
          ) : calculatedData ? (
            <Grid
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "start",
                maxHeight: "70vh",
              }}
              item
              xs={12}
              md={6}
            >
              <Typography
                sx={{ textAlign: "start", fontWeight: 600 }}
                variant="h6"
              >
                Calculation
              </Typography>

              <>
                <TableContainer sx={{ marginTop: 3 }} component={Paper}>
                  <Table>
                    <TableBody>
                      {Object.entries(calculatedData).map(([key, value]) => (
                        <TableRow key={key}>
                          <TableCell sx={{ fontWeight: "bold" }}>
                            {formatKey(key)}
                          </TableCell>
                          <TableCell>{formatValue(value)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            </Grid>
          ) : (
            <></>
          )}
        </Grid>
      </Box>
    </DashboardContainer>
  );
};

export default MarketDataCalculationForm;
